import { useEffect } from 'react';
import { isPlatformSafari, isPlatformIOS } from '@utils/common';
import { resolveComputedCssValues } from '@utils/css';
import { getHeightContainingLines } from './util.get-height-containing-lines.js';
import { useIsomorphicLayoutEffect } from '../../hooks/layout-effect.js';

/**
 * Safari fails to recognize line-clamping on elements that have block children
 * inside, instead it sets the height of the element to 0px. This is not what we
 * want.
 *
 * As such this hooks ensures that, on browsers which fails to set the correct
 * height, the height of the element is explicitly set to exactly the
 * height needed to render the desired amount of lines. Do note that the
 * trailing ellipsis (...) won't be present when using this fallback as Safari
 * fails to insert these as well.
 */
function useApplyMaxHeightWhenNecessary(props, wrapperRef) {
  useIsomorphicLayoutEffect(() => {
    const {
      maxLines
    } = typeof props.maxLines === "number" ? props : resolveComputedCssValues({
      maxLines: String(props.maxLines)
    }, wrapperRef.current ?? document.body);
    applyMaxHeightIfNecessary(Number(maxLines), wrapperRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.maxLines, wrapperRef]);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }
    const onChange = () => {
      const {
        maxLines
      } = typeof props.maxLines === "number" ? props : resolveComputedCssValues({
        maxLines: String(props.maxLines)
      }, wrapperRef.current ?? document.body);
      applyMaxHeightIfNecessary(Number(maxLines), wrapperRef);
    };
    const resizeObserver = new ResizeObserver(onChange);
    resizeObserver.observe(wrapper);
    const mutationObserver = new MutationObserver(onChange);
    mutationObserver.observe(wrapper, {
      childList: true,
      subtree: true
    });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  });
}
function applyMaxHeightIfNecessary(maxLines, wrapperRef) {
  if (!isPlatformSafari() && !isPlatformIOS()) {
    return;
  }
  const wrapper = wrapperRef.current;
  if (!wrapper) {
    return;
  }
  wrapper.style.height = "";

  // Safari fails to recognize line-clamping on elements that have block
  // children inside. In this case .clientHeight will compute to 0 when
  // line-height is set to a low number and .scrollHeight when line-clamping
  // is set to a VERY large number.
  if (wrapper.clientHeight === 0 && wrapper.textContent?.trim()) {
    wrapper.style.height = `${getHeightContainingLines(wrapper, maxLines)}px`;
  }
}

export { useApplyMaxHeightWhenNecessary };
//# sourceMappingURL=hook.apply-max-height-when-necessary.js.map
